import * as Sentry from '@sentry/react';

import { getEnvironment, isServer } from '../utils.mjs';

import { alarm as makeAlarm } from './alarm.js';
import { filterEvent } from './sentry-event-filters/index.js';

const {
  ERROR_REPORTING_VIA_CONSOLE = 'false',
  WB_ENV,
  SENTRY_DSN,
  FF_WBRT_5206_DEBUG_AUTH_SESSION,
} = getEnvironment();

// Sentry should not be used in tests, and breaks them anyway
const consoleLogger = [
  'debug',
  'info',
  'warning',
  'error',
  'fatal',
  'message',
  'alarm',
].reduce(
  (acc, level) =>
    Object.assign(acc, {
      // eslint-disable-next-line no-console
      [level]: (console[level] || console.log).bind(console),
    }),
  {}
);

Sentry.init({
  dsn: SENTRY_DSN,
  environment: WB_ENV,
  enableAutoSessionTracking: true,
  sessionTrackingIntervalMillis: 60000,
  normalizeDepth: 10,
  ignoreUrls: [
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/fbevents\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
  ],
  beforeSend(event, hint) {
    return filterEvent(event, hint);
  },
});

const mergeContext = (level, context) => ({
  level,
  extra: context,
});
const sendSentryEvent =
  (level = 'info') =>
  (error, context) => {
    const standardError = error instanceof Error ? error : new Error(error);

    Sentry.captureException(standardError, mergeContext(level, context));
  };

const exceptionLogger = {
  debug: sendSentryEvent('debug'),
  info: sendSentryEvent('info'),
  warning: sendSentryEvent('warning'),
  error: sendSentryEvent('error'),
  fatal: sendSentryEvent('fatal'),
  alarm: makeAlarm(sendSentryEvent('error')),
  message: Sentry.captureMessage,
  // A breadcrumb attaches to whatever error/message Sentry captures next in
  // this session (session replay too, where enabled) - it's not an event of
  // its own, so nothing shows up in Sentry from calling this alone.
  breadcrumb: (category, message, data) =>
    Sentry.addBreadcrumb({ category, message, data, level: 'info' }),
};

consoleLogger.breadcrumb = (category, message, data) =>
  // eslint-disable-next-line no-console
  console.info(`[breadcrumb:${category}] ${message}`, data);

export const logger =
  ERROR_REPORTING_VIA_CONSOLE == 'true' ? consoleLogger : exceptionLogger;

export const identifyLoggedUser = (userId) =>
  Sentry.getCurrentScope().setUser({
    id: userId,
  });

export const clearLoggedUser = () => Sentry.getCurrentScope().setUser(null);

/*
 * Debug logger for server to server auth session refresh calls.
 * Turn this on if sessions don't persist after reloading the page.
 */
export const logAuthSession = (message, ...args) => {
  if (FF_WBRT_5206_DEBUG_AUTH_SESSION !== 'true' || !isServer) return;

  consoleLogger.info(`WBRT_5206: ${message}`, ...args);
};
